import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar, TrendingUp, Clock, DollarSign, AlertCircle, FileText, ChevronLeft, ChevronRight, Edit3, Save, X, Layers, ExternalLink, Download, CheckSquare, Square, Filter } from "lucide-react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import axios from "axios";
import { formatDateRO } from "@/utils/dateFormat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper functions
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr);
};

const formatDate = (date) => {
  if (!date) return '-';
  return date.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateShort = (date) => {
  if (!date) return '-';
  return formatDateRO(date);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(value || 0);
};

const getDaysBetween = (start, end) => {
  if (!start || !end) return 0;
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const getTimeProgress = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  const now = new Date();
  
  if (now < start) return 0;
  if (now > end) return 100;
  
  const totalDays = getDaysBetween(start, end);
  const elapsedDays = getDaysBetween(start, now);
  
  return Math.round((elapsedDays / totalDays) * 100);
};

// Get status info
const getProjectStatus = (project) => {
  const timeProgress = getTimeProgress(project.start_date, project.end_date);
  const physicalProgress = project.progress_percent || 0;
  
  if (!project.start_date || !project.end_date) return { label: 'Nedefinit', color: '#6b7280' };
  if (physicalProgress >= 100) return { label: 'Finalizat', color: '#22c55e' };
  if (physicalProgress >= timeProgress) return { label: 'La timp', color: '#22c55e' };
  if (physicalProgress >= timeProgress - 10) return { label: 'Usor intarziat', color: '#eab308' };
  return { label: 'Intarziat', color: '#ef4444' };
};

// Quick Edit Popover Component
const QuickEditPopover = ({ project, children, onSave, companyId, onOpenPhases }) => {
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState({
    progress_percent: project.progress_percent || 0,
    expenses: project.expenses || 0
  });
  const [isSaving, setIsSaving] = useState(false);
  const [phasesCount, setPhasesCount] = useState(0);
  
  const timeProgress = getTimeProgress(project.start_date, project.end_date);
  const expectedExpenses = (project.total_value || 0) * (timeProgress / 100);
  
  // Fetch phases count when opened
  useEffect(() => {
    if (open && companyId && project.id) {
      axios.get(`${API}/sites/${project.id}/phases/summary?company_id=${companyId}`)
        .then(res => setPhasesCount(res.data.summary?.phase_count || 0))
        .catch(() => setPhasesCount(0));
    }
  }, [open, companyId, project.id]);
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/sites/${project.id}?company_id=${companyId}`, {
        progress_percent: editData.progress_percent,
        expenses: editData.expenses
      });
      
      toast.success('Progres actualizat!');
      setOpen(false);
      if (onSave) onSave();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Eroare la salvare');
    } finally {
      setIsSaving(false);
    }
  };
  
  const getProgressColor = () => {
    if (editData.progress_percent >= timeProgress) return 'text-green-600';
    if (editData.progress_percent >= timeProgress - 10) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="bottom">
        <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white p-3 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Edit3 className="w-4 h-4" />
              <span className="font-semibold">Editare Rapidă</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 w-6 p-0 text-white hover:bg-white/20"
              onClick={() => setOpen(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm opacity-90 mt-1 truncate">{project.name}</p>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Progress Slider */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                Stadiu Fizic
              </Label>
              <span className={`font-bold text-lg ${getProgressColor()}`}>
                {editData.progress_percent}%
              </span>
            </div>
            <Slider
              value={[editData.progress_percent]}
              onValueChange={(value) => setEditData({...editData, progress_percent: value[0]})}
              max={100}
              step={5}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>0%</span>
              <span className="text-blue-600">Timp: {timeProgress}%</span>
              <span>100%</span>
            </div>
            
            {/* Progress comparison */}
            <div className="flex gap-2 mt-2">
              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${editData.progress_percent >= timeProgress ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ width: `${editData.progress_percent}%` }}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${timeProgress}%` }}
                />
              </div>
            </div>
          </div>
          
          {/* Quick Progress Buttons */}
          <div className="flex gap-2">
            {[0, 25, 50, 75, 100].map(val => (
              <Button
                key={val}
                variant={editData.progress_percent === val ? "default" : "outline"}
                size="sm"
                className="flex-1 h-8 text-xs"
                onClick={() => setEditData({...editData, progress_percent: val})}
              >
                {val}%
              </Button>
            ))}
          </div>
          
          {/* Expenses */}
          {project.total_value > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <Label className="flex items-center gap-1">
                <DollarSign className="w-4 h-4 text-green-600" />
                Cheltuieli Reale (RON)
              </Label>
              <Input
                type="number"
                value={editData.expenses}
                onChange={(e) => setEditData({...editData, expenses: parseFloat(e.target.value) || 0})}
                className="h-9"
              />
              <div className="text-xs text-slate-500">
                Așteptat: {formatCurrency(expectedExpenses)} ({timeProgress}% din {formatCurrency(project.total_value)})
              </div>
              {editData.expenses > expectedExpenses && (
                <div className="text-xs text-red-600 font-medium">
                  ⚠️ Cheltuieli peste bugetul proporțional cu +{formatCurrency(editData.expenses - expectedExpenses)}
                </div>
              )}
            </div>
          )}
          
          {/* Save Button */}
          <Button 
            onClick={handleSave} 
            disabled={isSaving}
            className="w-full bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Se salvează...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvează
              </>
            )}
          </Button>
          
          {/* Open Phases Button */}
          {onOpenPhases && (
            <Button 
              variant="outline"
              onClick={() => {
                setOpen(false);
                onOpenPhases(project);
              }}
              className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <Layers className="w-4 h-4 mr-2" />
              Vezi Etape {phasesCount > 0 && `(${phasesCount})`}
              <ExternalLink className="w-3 h-3 ml-auto" />
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Timeline Bar component with edit capability
const TimelineBar = ({ project, startDate, totalDays, dayWidth, onEdit, companyId, onSave, onOpenPhases }) => {
  const projectStart = parseDate(project.start_date);
  const projectEnd = parseDate(project.end_date);
  
  if (!projectStart || !projectEnd) return null;
  
  const offsetDays = getDaysBetween(startDate, projectStart);
  const durationDays = getDaysBetween(projectStart, projectEnd) || 1;
  
  const left = offsetDays * dayWidth;
  const width = Math.max(durationDays * dayWidth, 60);
  
  const timeProgress = getTimeProgress(project.start_date, project.end_date);
  const physicalProgress = project.progress_percent || 0;
  
  const getStatusColor = () => {
    if (physicalProgress >= timeProgress) return 'bg-green-500';
    if (physicalProgress >= timeProgress - 10) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  
  return (
    <QuickEditPopover project={project} companyId={companyId} onSave={onSave} onOpenPhases={onOpenPhases}>
      <div 
        className="absolute top-2 h-16 rounded-lg shadow-md overflow-hidden transition-all hover:shadow-xl hover:scale-[1.02] cursor-pointer group"
        style={{ left: `${left}px`, width: `${width}px` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-200 to-slate-300" />
        
        {/* Physical Progress Bar (top half) */}
        <div 
          className={`absolute top-0 left-0 h-8 ${getStatusColor()} opacity-80 transition-all`}
          style={{ width: `${physicalProgress}%` }}
        />
        
        {/* Time Progress Bar (bottom half) */}
        <div 
          className="absolute bottom-0 left-0 h-8 bg-blue-500 opacity-60"
          style={{ width: `${timeProgress}%` }}
        />
        
        {/* Project Name */}
        <div className="absolute inset-0 flex items-center justify-center px-2">
          <span className="text-xs font-semibold text-slate-800 truncate drop-shadow-sm">
            {project.name}
          </span>
        </div>
        
        {/* Progress Labels */}
        <div className="absolute top-1 right-2 text-[10px] font-bold text-white drop-shadow">
          {physicalProgress}%
        </div>
        <div className="absolute bottom-1 right-2 text-[10px] font-medium text-white drop-shadow">
          {timeProgress}%
        </div>
        
        {/* Edit indicator on hover */}
        <div className="absolute top-1 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-white/90 rounded px-1.5 py-0.5 flex items-center gap-1 shadow">
            <Edit3 className="w-3 h-3 text-teal-600" />
            <span className="text-[9px] text-teal-700 font-medium">Click pentru editare</span>
          </div>
        </div>
      </div>
    </QuickEditPopover>
  );
};

// Project Card component
const ProjectCard = ({ project, onEdit }) => {
  const timeProgress = getTimeProgress(project.start_date, project.end_date);
  const physicalProgress = project.progress_percent || 0;
  const totalValue = project.total_value || 0;
  const expenses = project.expenses || 0;
  const expectedValue = totalValue * (timeProgress / 100);
  const status = getProjectStatus(project);
  
  return (
    <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-semibold text-lg">{project.name}</h3>
            <p className="text-sm text-slate-500">{project.address}</p>
          </div>
          <Badge style={{ backgroundColor: status.color }} className="text-white">{status.label}</Badge>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
          <Calendar className="w-4 h-4" />
          <span>
            {project.start_date ? formatDate(parseDate(project.start_date)) : 'Start nedefinit'} 
            {' → '}
            {project.end_date ? formatDate(parseDate(project.end_date)) : 'Final nedefinit'}
          </span>
        </div>
        
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                Stadiu Fizic
              </span>
              <span className="font-semibold text-green-600">{physicalProgress}%</span>
            </div>
            <Progress value={physicalProgress} className="h-3 bg-green-100" />
          </div>
          
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-blue-600" />
                Stadiu Timp
              </span>
              <span className="font-semibold text-blue-600">{timeProgress}%</span>
            </div>
            <Progress value={timeProgress} className="h-3 bg-blue-100" />
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <DollarSign className="w-3 h-3" />
                Valoare Asteptata
              </div>
              <p className="font-semibold text-slate-700">{formatCurrency(expectedValue)}</p>
              <p className="text-[10px] text-slate-400">({timeProgress}% din {formatCurrency(totalValue)})</p>
            </div>
            
            <div className={`rounded-lg p-2 ${expenses > expectedValue ? 'bg-red-50' : 'bg-green-50'}`}>
              <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                <DollarSign className="w-3 h-3" />
                Cheltuieli Reale
              </div>
              <p className={`font-semibold ${expenses > expectedValue ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(expenses)}
              </p>
              <p className="text-[10px] text-slate-400">
                {expenses > expectedValue ? (
                  <span className="text-red-500">+{formatCurrency(expenses - expectedValue)} peste</span>
                ) : (
                  <span className="text-green-500">-{formatCurrency(expectedValue - expenses)} sub</span>
                )}
              </p>
            </div>
          </div>
          
          <div className="mt-3 text-center">
            <span className="text-sm text-slate-500">Valoare Totala Proiect: </span>
            <span className="font-bold text-lg">{formatCurrency(totalValue)}</span>
          </div>
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full mt-4"
          onClick={() => onEdit && onEdit(project)}
        >
          Editeaza Proiect
        </Button>
      </CardContent>
    </Card>
  );
};

// PDF Export Function
const exportToPDF = (projects, companyName = "e-Tesa", showPhases = false, projectPhases = {}) => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  const colors = {
    primary: [13, 148, 136],
    secondary: [37, 99, 235],
    success: [34, 197, 94],
    warning: [234, 179, 8],
    danger: [239, 68, 68],
    dark: [30, 41, 59],
    light: [241, 245, 249],
    white: [255, 255, 255],
    phase: [147, 51, 234]
  };
  
  const projectsWithDates = projects.filter(p => p.start_date && p.end_date);
  
  // Generate filename - Format: "Proiect X by Companie" sau "Proiecte_Gantt_Companie"
  let fileName;
  const companyClean = companyName.replace(/[^a-zA-Z0-9\s\-]/g, '').trim().replace(/\s+/g, '_');
  if (projectsWithDates.length === 1) {
    const projectName = projectsWithDates[0].name.replace(/[^a-zA-Z0-9\s\-]/g, '').trim().replace(/\s+/g, '_');
    fileName = `${projectName}_by_${companyClean}.pdf`;
  } else if (projectsWithDates.length > 1) {
    fileName = `Proiecte_Gantt_${companyClean}.pdf`;
  } else {
    fileName = `Gantt_${companyClean}.pdf`;
  }
  
  if (projectsWithDates.length === 0) {
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Calendar Proiecte - Gantt Chart', 15, 18);
    doc.setFontSize(10);
    doc.text(`${companyName} | ${new Date().toLocaleDateString('ro-RO')}`, 15, 25);
    
    doc.setFontSize(14);
    doc.setTextColor(...colors.dark);
    doc.text('Nu exista proiecte cu date definite.', pageWidth / 2, 80, { align: 'center' });
    doc.save(fileName);
    return;
  }
  
  const allDates = projectsWithDates.flatMap(p => [new Date(p.start_date), new Date(p.end_date)]);
  const minProjectDate = new Date(Math.min(...allDates));
  const maxProjectDate = new Date(Math.max(...allDates));
  
  const timelineStart = new Date(minProjectDate.getFullYear(), minProjectDate.getMonth(), 1);
  const timelineEnd = new Date(maxProjectDate.getFullYear(), maxProjectDate.getMonth() + 1, 0);
  
  const totalDays = getDaysBetween(timelineStart, timelineEnd) + 1;
  
  const margins = { top: 45, bottom: 25, left: 15, right: 15 };
  const projectNameWidth = 55;
  const chartStartX = margins.left + projectNameWidth;
  const chartWidth = pageWidth - chartStartX - margins.right;
  const dayWidth = chartWidth / totalDays;
  
  const rowHeight = 18;
  const monthHeaderHeight = 8;
  const dayHeaderHeight = 6;
  const headerHeight = monthHeaderHeight + dayHeaderHeight + 2;
  
  // Header
  doc.setFillColor(...colors.primary);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Calendar Proiecte - Gantt Chart', margins.left, 15);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`${companyName}`, margins.left, 23);
  doc.text(`Generat: ${new Date().toLocaleDateString('ro-RO')}`, margins.left, 29);
  
  doc.setFontSize(9);
  doc.text(`Perioada: ${formatDateShort(timelineStart)} - ${formatDateShort(timelineEnd)} (${totalDays} zile)`, pageWidth - margins.right, 23, { align: 'right' });
  
  // Legend
  doc.setFillColor(...colors.success);
  doc.roundedRect(pageWidth - 100, 10, 8, 4, 1, 1, 'F');
  doc.setFontSize(8);
  doc.text('Stadiu Fizic', pageWidth - 90, 13);
  
  doc.setFillColor(...colors.secondary);
  doc.roundedRect(pageWidth - 100, 16, 8, 4, 1, 1, 'F');
  doc.text('Stadiu Timp', pageWidth - 90, 19);
  
  let currentY = margins.top;
  
  // Month headers row
  doc.setFillColor(...colors.light);
  doc.rect(chartStartX, currentY, chartWidth, monthHeaderHeight, 'F');
  
  let currentDate = new Date(timelineStart);
  while (currentDate <= timelineEnd) {
    const monthStart = new Date(currentDate);
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    const monthEnd = new Date(nextMonth - 1);
    
    const daysFromStart = getDaysBetween(timelineStart, monthStart);
    const daysInMonth = getDaysBetween(monthStart, monthEnd) + 1;
    
    const x = chartStartX + (daysFromStart * dayWidth);
    const width = Math.min(daysInMonth * dayWidth, chartWidth - (daysFromStart * dayWidth));
    
    doc.setDrawColor(180, 180, 180);
    doc.line(x, currentY, x, currentY + monthHeaderHeight);
    
    doc.setFontSize(7);
    doc.setTextColor(...colors.dark);
    const monthName = currentDate.toLocaleDateString('ro-RO', { month: 'short', year: '2-digit' });
    if (width > 15) {
      doc.text(monthName, x + 2, currentY + 5);
    }
    
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  
  // Days row
  currentY += monthHeaderHeight;
  doc.setFillColor(250, 250, 250);
  doc.rect(chartStartX, currentY, chartWidth, dayHeaderHeight, 'F');
  
  // Draw each day
  let dayDate = new Date(timelineStart);
  let dayIndex = 0;
  while (dayDate <= timelineEnd) {
    const x = chartStartX + (dayIndex * dayWidth);
    const dayNum = dayDate.getDate();
    const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
    const isToday = dayDate.toDateString() === new Date().toDateString();
    
    // Weekend/Today background
    if (isToday) {
      doc.setFillColor(...colors.secondary);
      doc.rect(x, currentY, dayWidth, dayHeaderHeight, 'F');
      doc.setTextColor(255, 255, 255);
    } else if (isWeekend) {
      doc.setFillColor(230, 230, 230);
      doc.rect(x, currentY, dayWidth, dayHeaderHeight, 'F');
      doc.setTextColor(150, 150, 150);
    } else {
      doc.setTextColor(100, 100, 100);
    }
    
    // Day number - only show if width allows
    doc.setFontSize(5);
    if (dayWidth >= 3) {
      doc.text(String(dayNum), x + dayWidth/2, currentY + 4, { align: 'center' });
    }
    
    // Day separator line
    doc.setDrawColor(220, 220, 220);
    doc.line(x, currentY, x, currentY + dayHeaderHeight);
    
    dayDate.setDate(dayDate.getDate() + 1);
    dayIndex++;
  }
  
  currentY += dayHeaderHeight + 2;
  
  // Project name header
  doc.setFillColor(240, 240, 240);
  doc.rect(margins.left, margins.top, projectNameWidth, monthHeaderHeight + dayHeaderHeight, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.dark);
  doc.text('PROIECT', margins.left + 2, margins.top + 8);
  
  // Projects
  projectsWithDates.forEach((project, index) => {
    if (currentY > pageHeight - margins.bottom - 20) {
      doc.addPage();
      currentY = 20;
    }
    
    const projectStart = new Date(project.start_date);
    const projectEnd = new Date(project.end_date);
    const daysFromStart = getDaysBetween(timelineStart, projectStart);
    const duration = getDaysBetween(projectStart, projectEnd) + 1;
    
    const barX = chartStartX + (daysFromStart * dayWidth);
    const barWidth = Math.max(duration * dayWidth, 20);
    
    const timeProgress = getTimeProgress(project.start_date, project.end_date);
    const physicalProgress = project.progress_percent || 0;
    const status = getProjectStatus(project);
    
    if (index % 2 === 0) {
      doc.setFillColor(250, 250, 250);
      doc.rect(margins.left, currentY, pageWidth - margins.left - margins.right, rowHeight, 'F');
    }
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.dark);
    const projectName = project.name.length > 22 ? project.name.substring(0, 22) + '..' : project.name;
    doc.text(projectName, margins.left + 2, currentY + 6);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5);
    doc.setTextColor(100, 100, 100);
    doc.text(`${formatDateShort(projectStart)} - ${formatDateShort(projectEnd)}`, margins.left + 2, currentY + 11);
    
    const statusColor = status.color === '#22c55e' ? colors.success : status.color === '#eab308' ? colors.warning : colors.danger;
    doc.setFillColor(...statusColor);
    doc.circle(margins.left + projectNameWidth - 5, currentY + 8, 2, 'F');
    
    doc.setFillColor(220, 220, 220);
    doc.roundedRect(barX, currentY + 2, barWidth, rowHeight - 4, 1, 1, 'F');
    
    if (physicalProgress > 0) {
      const progressColor = physicalProgress >= timeProgress ? colors.success : physicalProgress >= timeProgress - 10 ? colors.warning : colors.danger;
      doc.setFillColor(...progressColor);
      const progressWidth = Math.min(barWidth * (physicalProgress / 100), barWidth);
      doc.roundedRect(barX, currentY + 2, progressWidth, (rowHeight - 4) / 2, 1, 1, 'F');
    }
    
    if (timeProgress > 0) {
      doc.setFillColor(...colors.secondary);
      const timeWidth = Math.min(barWidth * (timeProgress / 100), barWidth);
      doc.roundedRect(barX, currentY + 2 + (rowHeight - 4) / 2, timeWidth, (rowHeight - 4) / 2, 1, 1, 'F');
    }
    
    if (barWidth > 30) {
      doc.setFontSize(6);
      doc.setTextColor(50, 50, 50);
      doc.text(`${physicalProgress}%`, barX + barWidth + 2, currentY + 7);
      doc.text(`${timeProgress}%`, barX + barWidth + 2, currentY + 12);
    }
    
    currentY += rowHeight;
  });
  
  // Summary
  const summaryY = Math.min(currentY + 10, pageHeight - 35);
  doc.setFillColor(...colors.light);
  doc.roundedRect(margins.left, summaryY, pageWidth - margins.left - margins.right, 20, 2, 2, 'F');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...colors.dark);
  doc.text('SUMAR', margins.left + 5, summaryY + 7);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  
  const totalProjects = projectsWithDates.length;
  const completedProjects = projectsWithDates.filter(p => (p.progress_percent || 0) >= 100).length;
  const onTrackProjects = projectsWithDates.filter(p => {
    const timeP = getTimeProgress(p.start_date, p.end_date);
    return (p.progress_percent || 0) >= timeP && (p.progress_percent || 0) < 100;
  }).length;
  const delayedProjects = totalProjects - completedProjects - onTrackProjects;
  const totalValue = projectsWithDates.reduce((sum, p) => sum + (p.total_value || 0), 0);
  
  doc.text(`Proiecte: ${totalProjects}`, margins.left + 5, summaryY + 14);
  doc.text(`Finalizate: ${completedProjects}`, margins.left + 45, summaryY + 14);
  doc.text(`La timp: ${onTrackProjects}`, margins.left + 85, summaryY + 14);
  doc.text(`Intarziate: ${delayedProjects}`, margins.left + 120, summaryY + 14);
  doc.text(`Valoare totala: ${formatCurrency(totalValue)}`, margins.left + 160, summaryY + 14);
  
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(`e-Tesa - Sistem de Management Constructii`, pageWidth / 2, pageHeight - 8, { align: 'center' });
  
  doc.save(fileName);
  toast.success(`PDF exportat: ${fileName}`);
};

export default function GanttChart({ projects, onEditProject, companyName, companyId, onRefresh, onOpenPhases }) {
  const scrollRef = useRef(null);
  const [viewMode, setViewMode] = useState('gantt');
  const [isExporting, setIsExporting] = useState(false);
  const [extraMonthsBefore, setExtraMonthsBefore] = useState(0);
  const [extraMonthsAfter, setExtraMonthsAfter] = useState(0);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  
  // State pentru selectarea proiectelor și opțiuni export
  const [selectedProjects, setSelectedProjects] = useState({});
  const [showPhases, setShowPhases] = useState(false);
  const [projectPhases, setProjectPhases] = useState({});
  const [showExportOptions, setShowExportOptions] = useState(false);
  
  // Inițializare selecție proiecte (toate selectate default)
  useEffect(() => {
    const initial = {};
    projects.forEach(p => {
      if (selectedProjects[p.id] === undefined) {
        initial[p.id] = true;
      }
    });
    if (Object.keys(initial).length > 0) {
      setSelectedProjects(prev => ({ ...prev, ...initial }));
    }
  }, [projects]);
  
  // Fetch phases for selected projects
  const fetchProjectPhases = async () => {
    if (!showPhases) return;
    
    const selectedIds = Object.entries(selectedProjects)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    
    const newPhases = {};
    for (const projectId of selectedIds) {
      try {
        const response = await axios.get(`${API}/phases/${projectId}`);
        newPhases[projectId] = response.data || [];
      } catch (error) {
        console.error(`Error fetching phases for ${projectId}:`, error);
        newPhases[projectId] = [];
      }
    }
    setProjectPhases(newPhases);
  };
  
  useEffect(() => {
    fetchProjectPhases();
  }, [showPhases, selectedProjects]);
  
  const toggleProjectSelection = (projectId) => {
    setSelectedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };
  
  const selectAllProjects = () => {
    const allSelected = {};
    projects.forEach(p => allSelected[p.id] = true);
    setSelectedProjects(allSelected);
  };
  
  const deselectAllProjects = () => {
    const allDeselected = {};
    projects.forEach(p => allDeselected[p.id] = false);
    setSelectedProjects(allDeselected);
  };
  
  const getFilteredProjects = () => {
    return projects.filter(p => selectedProjects[p.id]);
  };
  
  const handleExportPDF = () => {
    setIsExporting(true);
    try {
      const filteredProjects = getFilteredProjects();
      exportToPDF(filteredProjects, companyName, showPhases, projectPhases);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Eroare la exportul PDF');
    } finally {
      setIsExporting(false);
      setShowExportOptions(false);
    }
  };
  
  const handleSaveProgress = () => {
    if (onRefresh) onRefresh();
  };
  
  const getBaseTimelineRange = () => {
    const projectsWithDates = projects.filter(p => p.start_date && p.end_date);
    
    if (projectsWithDates.length === 0) {
      const now = new Date();
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 1, 0)
      };
    }
    
    const allDates = projectsWithDates.flatMap(p => [new Date(p.start_date), new Date(p.end_date)]);
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));
    
    const start = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const end = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
    
    return { start, end };
  };
  
  const getTimelineRange = () => {
    const base = getBaseTimelineRange();
    
    const start = new Date(base.start);
    start.setMonth(start.getMonth() - extraMonthsBefore);
    
    const end = new Date(base.end);
    end.setMonth(end.getMonth() + extraMonthsAfter);
    end.setMonth(end.getMonth() + 1, 0);
    
    return { start, end };
  };
  
  const { start: timelineStart, end: timelineEnd } = getTimelineRange();
  const totalDays = getDaysBetween(timelineStart, timelineEnd) + 1;
  const dayWidth = 30;
  
  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setIsAtStart(scrollLeft < 50);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 50);
    }
  };
  
  // Scroll listener on ref - intentionally runs once on mount
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => scrollEl.removeEventListener('scroll', handleScroll);
    }
  }); // No deps - re-attaches when ref changes on re-render
  
  const loadPreviousMonth = () => {
    setExtraMonthsBefore(prev => prev + 1);
    toast.info('Luna anterioară încărcată');
  };
  
  const loadNextMonth = () => {
    setExtraMonthsAfter(prev => prev + 1);
    toast.info('Luna următoare încărcată');
  };
  
  const getMonths = () => {
    const months = [];
    let current = new Date(timelineStart);
    
    while (current <= timelineEnd) {
      const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      
      months.push({
        date: monthStart,
        endDate: monthEnd,
        label: current.toLocaleDateString('ro-RO', { month: 'short', year: 'numeric' })
      });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };
  
  const getDays = () => {
    const days = [];
    let current = new Date(timelineStart);
    
    while (current <= timelineEnd) {
      days.push({
        date: new Date(current),
        dayNum: current.getDate(),
        isWeekend: current.getDay() === 0 || current.getDay() === 6,
        isToday: current.toDateString() === new Date().toDateString()
      });
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };
  
  const months = getMonths();
  const days = getDays();
  
  const projectsWithDates = projects.filter(p => p.start_date && p.end_date);
  const projectsWithoutDates = projects.filter(p => !p.start_date || !p.end_date);
  
  return (
    <div className="space-y-4">
      {/* View Toggle & Export Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex gap-2">
          <Button 
            variant={viewMode === 'gantt' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('gantt')}
            data-testid="gantt-view-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Gantt Chart
          </Button>
          <Button 
            variant={viewMode === 'cards' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('cards')}
            data-testid="cards-view-btn"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Carduri Proiecte
          </Button>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex gap-4 text-sm">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-500" />
              <span>Stadiu Fizic</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-500" />
              <span>Stadiu Timp</span>
            </div>
          </div>
          
          <Dialog open={showExportOptions} onOpenChange={setShowExportOptions}>
            <DialogTrigger asChild>
              <Button 
                disabled={isExporting || projects.length === 0}
                className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all"
                data-testid="export-pdf-btn"
              >
                {isExporting ? (
                  <>
                    <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Se exporta...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </>
                )}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-teal-700">
                  <Download className="w-5 h-5" />
                  Export Gantt Chart PDF
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Opțiune afișare etape */}
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium">Afișează și etapele</p>
                      <p className="text-xs text-slate-500">Include fazele fiecărui proiect</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPhases(!showPhases)}
                    className={`w-12 h-6 rounded-full transition-colors ${showPhases ? 'bg-purple-600' : 'bg-slate-300'}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${showPhases ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                
                {/* Selectare proiecte */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-teal-600" />
                      Selectează proiectele ({Object.values(selectedProjects).filter(Boolean).length}/{projects.length})
                    </Label>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={selectAllProjects} className="text-xs h-7">
                        Toate
                      </Button>
                      <Button variant="ghost" size="sm" onClick={deselectAllProjects} className="text-xs h-7">
                        Nimic
                      </Button>
                    </div>
                  </div>
                  <ScrollArea className="h-48 border rounded-lg p-2">
                    <div className="space-y-2">
                      {projects.map(project => (
                        <div 
                          key={project.id}
                          onClick={() => toggleProjectSelection(project.id)}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedProjects[project.id] 
                              ? 'bg-teal-50 border border-teal-200' 
                              : 'bg-slate-50 border border-transparent hover:bg-slate-100'
                          }`}
                        >
                          {selectedProjects[project.id] ? (
                            <CheckSquare className="w-5 h-5 text-teal-600" />
                          ) : (
                            <Square className="w-5 h-5 text-slate-400" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{project.name}</p>
                            {project.start_date && project.end_date && (
                              <p className="text-xs text-slate-500">
                                {new Date(project.start_date).toLocaleDateString('ro-RO')} - {new Date(project.end_date).toLocaleDateString('ro-RO')}
                              </p>
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {project.progress || 0}%
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
                
                {/* Butoane acțiuni */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    onClick={handleExportPDF}
                    disabled={isExporting || Object.values(selectedProjects).filter(Boolean).length === 0}
                    className="flex-1 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {isExporting ? 'Se exportă...' : 'Generează PDF'}
                  </Button>
                  <Button variant="outline" onClick={() => setShowExportOptions(false)}>
                    Anulează
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {/* Hint for quick edit */}
      {viewMode === 'gantt' && projectsWithDates.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 px-3 py-2 rounded-lg">
          <Edit3 className="w-4 h-4 text-teal-600" />
          <span>Click pe bara unui proiect pentru editare rapidă a progresului</span>
        </div>
      )}
      
      {viewMode === 'gantt' ? (
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardContent className="p-0 relative">
            {projectsWithDates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <AlertCircle className="w-12 h-12 mb-4 text-slate-400" />
                <p className="text-lg font-medium">Niciun proiect cu date definite</p>
                <p className="text-sm">Adaugati date de inceput si sfarsit pentru a vedea timeline-ul</p>
              </div>
            ) : (
              <>
                {isAtStart && (
                  <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={loadPreviousMonth}
                      className="h-full rounded-none bg-gradient-to-r from-slate-100 to-transparent hover:from-slate-200 px-2"
                    >
                      <ChevronLeft className="w-5 h-5 text-slate-600" />
                      <span className="text-xs hidden sm:block">Luna anterioară</span>
                    </Button>
                  </div>
                )}
                
                {isAtEnd && (
                  <div className="absolute right-0 top-0 bottom-0 z-20 flex items-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={loadNextMonth}
                      className="h-full rounded-none bg-gradient-to-l from-slate-100 to-transparent hover:from-slate-200 px-2"
                    >
                      <span className="text-xs hidden sm:block">Luna următoare</span>
                      <ChevronRight className="w-5 h-5 text-slate-600" />
                    </Button>
                  </div>
                )}
                
                <div 
                  className="overflow-x-auto" 
                  ref={scrollRef}
                  onScroll={handleScroll}
                >
                  <div className="sticky top-0 bg-white z-10 border-b border-slate-200">
                    <div className="flex" style={{ width: `${totalDays * dayWidth}px` }}>
                      {months.map((month, idx) => {
                        const daysInMonth = getDaysBetween(month.date, month.endDate) + 1;
                        return (
                          <div
                            key={month.label}
                            className="border-r border-slate-200 bg-slate-50 px-2 py-1 text-sm font-medium"
                            style={{ width: `${daysInMonth * dayWidth}px` }}
                          >
                            {month.label}
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="flex" style={{ width: `${totalDays * dayWidth}px` }}>
                      {days.map((day, idx) => (
                        <div
                          key={day.label + '-' + idx}
                          className={`text-center text-xs border-r border-slate-100 py-1 ${
                            day.isToday 
                              ? 'bg-blue-500 text-white font-bold' 
                              : day.isWeekend 
                                ? 'bg-slate-100 text-slate-400' 
                                : 'text-slate-600'
                          }`}
                          style={{ width: `${dayWidth}px` }}
                        >
                          {day.dayNum}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="relative" style={{ width: `${totalDays * dayWidth}px` }}>
                    {projectsWithDates.map((project) => (
                      <div
                        key={project.id}
                        className="relative h-20 border-b border-slate-100 hover:bg-slate-50"
                      >
                        {days.findIndex(d => d.isToday) >= 0 && (
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-5"
                            style={{ left: `${days.findIndex(d => d.isToday) * dayWidth + dayWidth / 2}px` }}
                          />
                        )}
                        
                        <TimelineBar 
                          project={project} 
                          startDate={timelineStart} 
                          totalDays={totalDays}
                          dayWidth={dayWidth}
                          companyId={companyId}
                          onSave={handleSaveProgress}
                          onOpenPhases={onOpenPhases}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(project => (
            <ProjectCard 
              key={project.id} 
              project={project} 
              onEdit={onEditProject}
            />
          ))}
        </div>
      )}
      
      {viewMode === 'gantt' && projectsWithoutDates.length > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">
                  {projectsWithoutDates.length} proiect(e) fara date definite:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {projectsWithoutDates.map(p => (
                    <Badge 
                      key={p.id} 
                      variant="outline" 
                      className="cursor-pointer hover:bg-yellow-100"
                      onClick={() => onEditProject && onEditProject(p)}
                    >
                      {p.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
